/**
 * ChatBot Service
 * Handles all interactions with the Flexit RAG API
 */


import { ChatContext, ChatInitContentItem, ChatInitResponse, ChatResponse, ChatMessage, FeedbackQuestionsResponse, FeedbackSubmissionPayload, FeedbackSubmissionResult } from '../types';
import { CHATBOT_CONFIG } from '../config';
import { capturePageContext } from '../utils/contextCapture';
import { appendStreamText, isSseContentType, normalizeSseChunk, parseSseEvent, splitSseFrames } from '../utils/streamUtils';
import { RootState } from '../../../reducers';
import { axios, API_BASE_URL } from '../../../utils';
import { DEFAULT_HEADER } from '../../../utils/Storage';
import { ENDPOINTS } from "../../../constants/APIConstants";
import { clearAllChatBotData, loadChatSessionReadyState } from '../utils/storage';

const buildApiUrl = (endpoint: string): string => {
  const normalizedBaseUrl = API_BASE_URL.replace(/\/+$/, '');
  const normalizedEndpoint = endpoint.replace(/^\/+/, '');
  return `${normalizedBaseUrl}/${normalizedEndpoint}`;
};

interface AskStreamOptions {
  signal?: AbortSignal;
  onToken?: (chunk: string) => void;
  errorFallbackMessage?: string;
}

interface AskPayloadContext {
  pageUrl?: string;
  pageTitle?: string;
  userAction?: string;
  additionalData?:any
}

interface ChatApiEnvelope {
  version?: string;
  Version?: string;
  statusCode?: number | string;
  StatusCode?: number | string;
  errorMessage?: string;
  ErrorMessage?: string;
  message?: string;
}

interface ChatConfigurationsApiResponse extends ChatApiEnvelope {
  isChatEnabled?: boolean;
  greetings?: string;
  disclaimer?: string;
  contentResource?: {
    contents?: ChatInitContentItem[];
    controlResource?: Record<string, unknown>;
  };
}

interface ChatNewSessionApiResponse extends ChatApiEnvelope {
  sessionCreated?: boolean;
}

interface ChatCloseSessionApiResponse extends ChatApiEnvelope {}

class ChatBotService {

  public async getInitData(): Promise<ChatInitResponse> {
    try {
      const response = await axios<any>({
        method: 'GET',
        url: ENDPOINTS.CHATBOT.configurations,
        skipAppLoader: true,
      } as any);

      const apiResponse: ChatConfigurationsApiResponse = response?.data ?? response ?? {};
      const resolvedStatusCode = Number(
        apiResponse.statusCode ?? apiResponse.StatusCode ?? response?.status ?? 500
      );
      const contentResource = apiResponse.contentResource ?? {
        contents: [],
        controlResource: {},
      };

      if (resolvedStatusCode !== 200) {
        return {
          isChatEnabled: false,
          contentResource,
          statusCode: resolvedStatusCode,
        };
      }

      return {
        isChatEnabled: apiResponse.isChatEnabled,
        greetings: apiResponse.greetings,
        disclaimer: apiResponse.disclaimer,
        contentResource,
        statusCode: resolvedStatusCode,
        version: apiResponse.version ?? apiResponse.Version,
      };
    } catch (error) {
      console.error('ChatBot Init API Error:', error);
      return {
        isChatEnabled: false,
        contentResource: {
          contents: [],
          controlResource: {},
        },
        result: {},
        statusCode: 500,
      };
    }
  }

  public async startSession(): Promise<boolean> {
    try {
      const response = await axios<any>({
        method: 'POST',
        url: ENDPOINTS.CHATBOT.newSession,
        data: {},
        headers: {
          'Content-Type': 'application/json',
        },
        skipAppLoader: true,
      } as any);

      const payload: ChatNewSessionApiResponse = response?.data ?? response ?? {};
      const resolvedStatusCode = Number(payload.statusCode ?? payload.StatusCode ?? response?.status ?? 500);
      if (resolvedStatusCode !== 200) {
        return false;
      }

      return payload.sessionCreated !== false;
    } catch (error) {
      console.error('ChatBot New Session API Error:', error);
      return false;
    }
  }

  public async closeSession(): Promise<boolean> {
   
    try {
      const response = await axios<any>({
        method: 'POST',
        url: ENDPOINTS.CHATBOT.closeSession,
        data: {},
        headers: {
          'Content-Type': 'application/json',
        },
        skipAppLoader: true,
      } as any);

      const payload: ChatCloseSessionApiResponse = response?.data ?? response ?? {};
      const resolvedStatusCode = Number(payload.statusCode ?? payload.StatusCode ?? response?.status ?? 500);
      return resolvedStatusCode === 200;
    } catch (error) {
      console.error('ChatBot Close Session API Error:', error);
      return false;
    }
  }

  public async sendMessage(
    message: string,
    includeContext = false,
    reduxState?: RootState,
    options?: AskStreamOptions
  ): Promise<ChatResponse> {
    try {
      const fallbackMessage = options?.errorFallbackMessage||"";
      const context = includeContext
        ? capturePageContext(reduxState, CHATBOT_CONFIG.enablePageContentCapture)
        : undefined;

      const headers: Record<string, string> = {
        ...DEFAULT_HEADER.get(),
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      };

      const requestBody: { question: string; context?: AskPayloadContext } = {
        question: message,
      };

      const mappedContext = this.mapAskContext(context);
      if (mappedContext) {
        requestBody.context = mappedContext;
      }

      const response = await fetch(buildApiUrl(ENDPOINTS.CHATBOT.ask), {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: options?.signal,
      });

      if (!response.ok || !response.body) {
        if (response.status === 401 || response.status === 403 || response.status >= 500) {
          return {
            response: fallbackMessage,
            message: fallbackMessage,
            status: 'error',
          };
        }

        const errorMessage = await this.extractErrorMessage(response, fallbackMessage);
        return {
          response: errorMessage,
          message: errorMessage,
          status: 'error'
        };
      }

      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      if (!isSseContentType(contentType)) {
        const nonStreamMessage = await this.extractErrorMessage(response, fallbackMessage);
        return {
          response: nonStreamMessage,
          message: nonStreamMessage,
          status: 'error',
        };
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let combinedResponse = '';
      let isDone = false;

      while (!isDone) {
        const { value, done } = await reader.read();

        if (done) {
          break;
        }

        buffer += normalizeSseChunk(decoder.decode(value, { stream: true }));

        const { frames, rest } = splitSseFrames(buffer);
        buffer = rest;

        for (const rawEvent of frames) {
          const parsed = parseSseEvent(rawEvent);
          if (parsed.isDone) {
            isDone = true;
            break;
          }

          if (parsed.data) {
            combinedResponse = appendStreamText(combinedResponse, parsed.data);
            options?.onToken?.(parsed.data);
          }
        }
      }

      return {
        response: combinedResponse,
        status: 'success',
      };
    } catch (error: any) {
      const fallbackMessage = options?.errorFallbackMessage||"";
      if (error?.name === 'AbortError') {
        return {
          response: '',
          status: 'error',
          message: fallbackMessage,
        };
      }

      console.error('ChatBot Ask SSE Error:', error);
      return {
        response: fallbackMessage,
        status: 'error',
        message: fallbackMessage,
      };
    }
  }

  /**
   * Create a chat message object
   */
  public createMessage(
    role: 'user' | 'assistant',
    content: string,
    status: 'sending' | 'sent' | 'error' = 'sent',
    options?: {
      contentFormat?: 'markdown' | 'html';
      action?: 'retry_last_message' | 'system_error_retry' | 'chat_connect_close';
    }
  ): ChatMessage {
    return {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role,
      content,
      timestamp: new Date(),
      status,
      contentFormat: options?.contentFormat,
      action: options?.action,
    };
  }

  public async getFeedbackQuestions(): Promise<FeedbackQuestionsResponse | null> {
    try {
      const response = await axios<any>({
        method: 'GET',
        url: ENDPOINTS.CHATBOT.feedback,
      } as any);

      const payload: FeedbackQuestionsResponse & ChatApiEnvelope = response?.data ?? response ?? {};
      const resolvedStatusCode = Number(payload.statusCode ?? payload.StatusCode ?? response?.status ?? 500);

      if (resolvedStatusCode !== 200) {
        return null;
      }

      return {
        feedbackSurveyID: payload.feedbackSurveyID,
        questions: payload.questions ?? [],
        StatusCode: resolvedStatusCode,
        Version: payload.Version ?? payload.version,
      };
    } catch (error) {
      console.error('ChatBot Feedback Questions API Error:', error);
      return null;
    }
  }

  public async submitFeedback(payload: FeedbackSubmissionPayload): Promise<FeedbackSubmissionResult> {
    try {
      const response = await axios<any>({
        method: 'POST',
        url: ENDPOINTS.CHATBOT.feedback,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
        }
      } as any);

      const responseData: ChatApiEnvelope = response?.data ?? response ?? {};
      const resolvedStatusCode = Number(responseData.statusCode ?? responseData.StatusCode ?? response?.status ?? 500);
      if (resolvedStatusCode === 200) {
        return { success: true };
      }

      return {
        success: false,
        errorMessage: responseData.errorMessage || responseData.ErrorMessage || responseData.message,
      };
    } catch (error: any) {
      console.error('ChatBot Submit Feedback API Error:', error);
      const serverPayload = error?.response?.data ?? {};
      return {
        success: false,
        errorMessage: serverPayload?.errorMessage || serverPayload?.ErrorMessage || serverPayload?.message,
      };
    }
  }

  public clearAllChatBotDataService(){
     if(loadChatSessionReadyState()){
      this.closeSession();
    }
    clearAllChatBotData();
  }

  private mapAskContext(context?: ChatContext): AskPayloadContext | undefined {
    if (!context) {
      return undefined;
    }

    const mapped: AskPayloadContext = {
      pageUrl: context.pageUrl,
      pageTitle: context.pageTitle,
      userAction: context.userAction,
      additionalData: context.additionalData
    };

    const hasValue = Object.values(mapped).some((value) => typeof value === 'string' && value.trim().length > 0);
    return hasValue ? mapped : undefined;
  }

  private async extractErrorMessage(response: Response, fallbackMessage: string): Promise<string> {
    try {
      const errorData = await response.json();
      const serverMessage = errorData?.errorMessage || errorData?.ErrorMessage || errorData?.message;
      if (typeof serverMessage === 'string' && serverMessage.trim()) {
        return serverMessage;
      }
    } catch (error) {
      console.warn('Failed to parse chatbot SSE error payload:', error);
    }

    return fallbackMessage;
  }
}

// Export singleton instance
export const chatBotService = new ChatBotService();
export default chatBotService;
